import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  TrackingAttributes,
  TrackVisible,
} from '../tracking/certificatesTracking'

type ContextContent = {
  event?: React.ComponentProps<typeof TrackVisible>['event']
  listKey?: string
}

const TrackingRowContext = React.createContext<ContextContent>({})

export const TrackingRowProvider = ({
  children,
  event,
  listKey,
}: {
  children: React.ReactNode
  event: React.ComponentProps<typeof TrackVisible>['event']
  listKey?: string
}) => (
  <TrackingRowContext.Provider value={{ event, listKey }}>
    {children}
  </TrackingRowContext.Provider>
)

type PropsType = {
  children: React.ReactElement
  /**
   * When false, the row keeps its DOM position (so `row_position_index` stays
   * stable) but does not observe visibility yet. Use for async content so the
   * viewed event fires only once the real content has rendered, not on the
   * empty placeholder. Defaults to true.
   */
  contentReady?: boolean
} & Pick<React.ComponentProps<typeof TrackVisible>, 'visibilityConfig'> &
  React.ComponentProps<typeof TrackingAttributes>

export const TrackingRow = ({
  children,
  visibilityConfig,
  contentReady = true,
  ...rest
}: PropsType) => {
  const [rowPositionIndex, setRowPositionIndex] = useState(-1)
  const { event, listKey } = useContext(TrackingRowContext)
  const ref = useRef<HTMLDivElement>(null)

  const dataRowType = listKey ?? event

  useEffect(() => {
    const getRowPositionIndex = () => {
      const rows = Array.from(
        document.querySelectorAll<HTMLElement>(
          `[data-row-type="${dataRowType}"]`,
        ),
      )
      return ref.current ? rows.indexOf(ref.current) : -1
    }

    setRowPositionIndex(getRowPositionIndex())
  }, [dataRowType])

  if (!event)
    return (
      <TrackingAttributes row_position_index={rowPositionIndex} {...rest}>
        {children}
      </TrackingAttributes>
    )

  return (
    <TrackingAttributes row_position_index={rowPositionIndex} {...rest}>
      <div ref={ref} data-row-type={dataRowType}>
        {contentReady ? (
          <TrackVisible event={event} visibilityConfig={visibilityConfig}>
            {children}
          </TrackVisible>
        ) : (
          children
        )}
      </div>
    </TrackingAttributes>
  )
}
